import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useRef, useState } from 'react';
import { Button, FormControl, FormHelperText, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Icon } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { normalizeName, parseCsvText } from '../submeterCsv';
const CELL_BORDER = '1px solid #e0e0e0';
const groupByNormalizedName = names => names.reduce((groups, name) => {
  var _groups$get;
  const key = normalizeName(name);
  groups.set(key, [...((_groups$get = groups.get(key)) !== null && _groups$get !== void 0 ? _groups$get : []), name]);
  return groups;
}, new Map());

// One CSV value goes to one Livable unit: a name that matches more than one unit, or more than
// one CSV value, is ambiguous and is not pre-filled.
// csvCells are the distinct cells before trimming, so "Apt A" and " Apt A " count as two values.
const findUnambiguousMatches = (unitNames, csvCells) => {
  const unitGroups = groupByNormalizedName(unitNames);
  const csvGroups = groupByNormalizedName(csvCells);
  return unitNames.reduce((matches, unitName) => {
    const key = normalizeName(unitName);
    const csvMatches = csvGroups.get(key);
    if (unitGroups.get(key).length === 1 && (csvMatches === null || csvMatches === void 0 ? void 0 : csvMatches.length) === 1) matches.set(unitName, csvMatches[0].trim());
    return matches;
  }, new Map());
};

// Unit names are user data, so read the mapping by own key only ("constructor" is a valid name).
const getMapped = (mappings, unitName) => Object.prototype.hasOwnProperty.call(mappings, unitName) ? mappings[unitName] : '';
const fillEmptyUnits = (current, matches) => {
  const taken = new Set(Object.values(current).filter(Boolean).map(normalizeName));
  return [...matches].reduce((filled, _ref) => {
    let _ref2 = _slicedToArray(_ref, 2),
      unitName = _ref2[0],
      csvValue = _ref2[1];
    if (getMapped(filled, unitName) || taken.has(normalizeName(csvValue))) return filled;
    taken.add(normalizeName(csvValue));
    return _objectSpread(_objectSpread({}, filled), {}, {
      [unitName]: csvValue
    });
  }, _objectSpread({}, current));
};
export const MapUnitNamesSection = observer(_ref3 => {
  var _store$homeDashboardS, _store$homeDashboardS2;
  let dataRef = _ref3.dataRef,
    onValidChange = _ref3.onValidChange,
    initialUnitMappings = _ref3.initialUnitMappings;
  const store = useStore();
  const _useState = useState(initialUnitMappings || {}),
    _useState2 = _slicedToArray(_useState, 2),
    unitMappings = _useState2[0],
    setUnitMappings = _useState2[1];
  const _useState3 = useState(() => Object.values(initialUnitMappings || {}).some(Boolean)),
    _useState4 = _slicedToArray(_useState3, 1),
    hasSavedMapping = _useState4[0];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    csvFile = _useState6[0],
    setCsvFile = _useState6[1];
  const activeFile = useRef(null);
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    csvHeaders = _useState8[0],
    setCsvHeaders = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    csvRows = _useState0[0],
    setCsvRows = _useState0[1];
  const _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    csvRawRows = _useState10[0],
    setCsvRawRows = _useState10[1];
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    unitNameColumn = _useState12[0],
    setUnitNameColumn = _useState12[1];
  const _useState13 = useState([]),
    _useState14 = _slicedToArray(_useState13, 2),
    csvUnitOptions = _useState14[0],
    setCsvUnitOptions = _useState14[1];
  const _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    isDragging = _useState16[0],
    setIsDragging = _useState16[1];
  const _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    showErrors = _useState18[0],
    setShowErrors = _useState18[1];
  const fileInputRef = useRef(null);
  const unitNames = ((_store$homeDashboardS = (_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.selectedPropertyDetails()) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.details) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.unitSetup) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.units) !== null && _store$homeDashboardS !== void 0 ? _store$homeDashboardS : []).map(u => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, {
    numeric: true
  }));

  // Partial mapping is allowed — units left empty simply won't be mapped.
  // Only the CSV unit-name column selection is required (when a file is uploaded).
  const isColumnValid = !csvFile || !!unitNameColumn;
  const isValid = isColumnValid;

  // Sync validity to parent synchronously (same pattern as dataRef)
  onValidChange === null || onValidChange === void 0 || onValidChange(isValid);

  // Sync data + validate fn to parent ref
  if (dataRef) {
    dataRef.current = {
      unitMappings,
      validate: () => {
        setShowErrors(true);
        return isValid;
      }
    };
  }
  const parseCsvFile = async file => {
    try {
      const text = await file.text();
      if (activeFile.current !== file) return;
      const _parseCsvText = parseCsvText(text),
        headers = _parseCsvText.headers,
        rawRows = _parseCsvText.rawRows,
        rows = _parseCsvText.rows;
      setCsvHeaders(headers);
      setCsvRawRows(rawRows);
      setCsvRows(rows);
    } catch (_unused) {
      if (activeFile.current !== file) return;
      setCsvHeaders([]);
      setCsvRawRows([]);
      setCsvRows([]);
    }
  };
  const getUnitOptions = (rows, headers, column) => {
    const colIdx = headers.indexOf(column);
    if (colIdx === -1) return [];
    return [...new Set(rows.map(row => row[colIdx]).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, {
      numeric: true
    }));
  };
  const getDistinctRawCells = (rows, headers, column) => {
    const colIdx = headers.indexOf(column);
    if (colIdx === -1) return [];
    return [...new Set(rows.map(row => row[colIdx]).filter(cell => cell === null || cell === void 0 ? void 0 : cell.trim()))];
  };
  const handleFileSelect = async file => {
    activeFile.current = file;
    setCsvFile(file);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvRawRows([]);
    setUnitNameColumn('');
    setCsvUnitOptions([]);
    if (!hasSavedMapping) setUnitMappings({});
    await parseCsvFile(file);
  };
  const handleFileInputChange = async e => {
    var _e$target$files;
    const file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (file) await handleFileSelect(file);
  };
  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = async e => {
    var _e$dataTransfer$files;
    e.preventDefault();
    setIsDragging(false);
    const file = (_e$dataTransfer$files = e.dataTransfer.files) === null || _e$dataTransfer$files === void 0 ? void 0 : _e$dataTransfer$files[0];
    if (file) await handleFileSelect(file);
  };
  const handleRemoveFile = () => {
    activeFile.current = null;
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvRawRows([]);
    setUnitNameColumn('');
    setCsvUnitOptions([]);
    if (!hasSavedMapping) setUnitMappings({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleColumnChange = column => {
    const options = getUnitOptions(csvRows, csvHeaders, column);
    setUnitNameColumn(column);
    setCsvUnitOptions(options);
    const isDuplicateHeader = csvHeaders.filter(header => header === column).length > 1;
    const matches = isDuplicateHeader ? new Map() : findUnambiguousMatches(unitNames, getDistinctRawCells(csvRawRows, csvHeaders, column));
    setUnitMappings(prev => fillEmptyUnits(hasSavedMapping ? prev : {}, matches));
  };
  const filledUnitCount = unitNames.filter(unitName => getMapped(unitMappings, unitName)).length;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 24
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    style: {
      fontWeight: 600,
      marginBottom: 4
    }
  }, "Map unit names"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "light",
    style: {
      marginBottom: 4
    }
  }, "Map the unit names in Livable to the names of the units in your CSV file"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    style: {
      marginBottom: 16
    }
  }, "Changed this after reads were saved? Recalculate on Meter details to remove charges from units that are no longer mapped. Newly mapped units won't be added to a cycle that already has saved rows."), /*#__PURE__*/React.createElement("div", {
    style: {
      border: "1px dashed ".concat(isDragging ? '#1976d2' : '#b8c4ce'),
      borderRadius: 4,
      padding: csvFile ? 12 : 48,
      cursor: !csvFile ? 'pointer' : 'default',
      marginBottom: 16,
      backgroundColor: isDragging ? 'rgba(25,118,210,0.04)' : undefined
    },
    onClick: () => {
      var _fileInputRef$current;
      return !csvFile && ((_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click());
    },
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop
  }, !csvFile ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    align: "center",
    color: "light"
  }, "Upload a CSV to auto-detect unit names (optional)") : /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-text"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: 600
    }
  }, csvFile.name), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    size: "small",
    style: {
      minWidth: 0
    },
    onClick: e => {
      e.stopPropagation();
      handleRemoveFile();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".csv",
    hidden: true,
    onChange: handleFileInputChange
  })), csvHeaders.some(Boolean) && /*#__PURE__*/React.createElement(FormControl, {
    size: "small",
    fullWidth: true,
    error: showErrors && !isColumnValid,
    style: {
      marginBottom: 16
    }
  }, /*#__PURE__*/React.createElement(Select, {
    displayEmpty: true,
    value: unitNameColumn,
    onChange: e => handleColumnChange(e.target.value),
    renderValue: val => val ? val : /*#__PURE__*/React.createElement("span", {
      style: {
        color: '#9e9e9e'
      }
    }, "Select unit name column")
  }, [...new Set(csvHeaders.filter(Boolean))].map(h => /*#__PURE__*/React.createElement(MenuItem, {
    key: h,
    value: h
  }, h))), showErrors && !isColumnValid && /*#__PURE__*/React.createElement(FormHelperText, null, "Required")), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      borderBottom: CELL_BORDER,
      paddingBottom: 8
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 12px 0 0',
      borderRight: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600,
      color: '#6b7a8d'
    }
  }, "Livable unit names")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 0 0 12px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600,
      color: '#6b7a8d'
    }
  }, "CSV unit names"))), (unitNameColumn || filledUnitCount > 0) && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    style: {
      margin: '8px 0'
    }
  }, "".concat(filledUnitCount, " of ").concat(unitNames.length, " units have a CSV name. Check them and pick the rest.")), unitNames.map((unitName, idx) => {
    return /*#__PURE__*/React.createElement("div", {
      key: unitName,
      style: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: idx < unitNames.length - 1 ? CELL_BORDER : undefined
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '8px 12px 8px 0',
        borderRight: CELL_BORDER
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, unitName)), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '4px 0 4px 12px'
      }
    }, csvUnitOptions.length > 0 ? /*#__PURE__*/React.createElement(FormControl, {
      size: "small",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Select, {
      displayEmpty: true,
      value: getMapped(unitMappings, unitName),
      onChange: e => setUnitMappings(prev => _objectSpread(_objectSpread({}, prev), {}, {
        [unitName]: e.target.value
      })),
      renderValue: val => val ? val : /*#__PURE__*/React.createElement("span", {
        style: {
          color: '#9e9e9e'
        }
      }, "Select unit")
    }, /*#__PURE__*/React.createElement(MenuItem, {
      value: ""
    }, "None"), (csvUnitOptions.includes(getMapped(unitMappings, unitName)) ? csvUnitOptions : [...csvUnitOptions, getMapped(unitMappings, unitName)].filter(Boolean)).filter(u => !Object.entries(unitMappings).some(_ref4 => {
      let _ref5 = _slicedToArray(_ref4, 2),
        n = _ref5[0],
        v = _ref5[1];
      return n !== unitName && v === u;
    })).map(u => /*#__PURE__*/React.createElement(MenuItem, {
      key: u,
      value: u
    }, u)))) : /*#__PURE__*/React.createElement(TextField, {
      size: "small",
      fullWidth: true,
      placeholder: "Unit name",
      value: getMapped(unitMappings, unitName),
      onChange: e => setUnitMappings(prev => _objectSpread(_objectSpread({}, prev), {}, {
        [unitName]: e.target.value
      }))
    })));
  }));
});